import React from 'react';
import ArtistPage from '@/pages/ArtistPage';
import DAOMemberPage from '@/pages/DAOMemberPage';
import DAOProposalPage from '@/pages/DAOProposalPage';
import DaoGovernorPage from '@/pages/DaoGovernorPage';
import EventPage from '@/pages/EventPage';
import EventTicketPage from '@/pages/EventTicketPage';
import HRDPage from '@/pages/HRDPage';
import OrganizerPage from '@/pages/OrganizerPage';
import RevellerPage from '@/pages/RevellerPage';
import ServiceProviderPage from '@/pages/ServiceProviderPage';
import SponsorPage from '@/pages/SponsorPage';
import SupportRequestsPage from '@/pages/SupportRequestsPage';
import TreasuryPage from '@/pages/TreasuryPage';
import VenuePage from '@/pages/VenuePage';
import ProposalPage from '@/pages/ProposalPage';
import RosterBookingPage from '@/pages/RosterBookingPage';
import MarketplacePage from '@/pages/MarketplacePage';
import RosterPage from '@/pages/RosterPage';
import OnboardingPage from '@/pages/OnboardingPage';
import MySupportTickets from '@/components/MySupportTickets';
import { UserRole, OnboardingRequest } from '@/types';
import RosterAnalyticsPage from '@/pages/RosterAnalyticsPage';
import RosterRealDataTable from '@/components/tables/RosterRealDataTable';
import ContractFactoryRealDataTable from '@/components/tables/ContractFactoryRealDataTable';
import ContractsDashboard from '@/components/ContractsDashboard';
import { Users, Calendar, MapPin, Store, Ticket, FileSignature, MessageSquare, Landmark, UserPlus, Table } from 'lucide-react';


interface TablesPageProps {
  activeView: string;
  onNavigate: (view: string) => void;
  currentUserRole: UserRole;
  currentUserId: string;
  requests: OnboardingRequest[];
  onStatusChange: (id: string, status: 'Approved' | 'Denied') => void;
  onDelete: (id: string, type: 'Lead' | 'Join Request') => void;
}

const ALL_USER_ROLES = Object.values(UserRole);

const allTableNavItems = [
    { view: 'artists', label: 'Artists', icon: <Users size={28}/>, roles: ALL_USER_ROLES },
    { view: 'events', label: 'Events', icon: <Calendar size={28}/>, roles: ALL_USER_ROLES },
    { view: 'venues', label: 'Venues', icon: <MapPin size={28}/>, roles: ALL_USER_ROLES },
    { view: 'organizers', label: 'Organizers', icon: <Users size={28}/>, roles: ALL_USER_ROLES },
    { view: 'sponsors', label: 'Sponsors', icon: <Users size={28}/>, roles: ALL_USER_ROLES },
    { view: 'revellers', label: 'Revellers', icon: <Users size={28}/>, roles: ALL_USER_ROLES },
    { view: 'service-providers', label: 'Service Providers', icon: <Users size={28}/>, roles: ALL_USER_ROLES },
    { view: 'marketplace-table', label: 'Marketplace Items', icon: <Store size={28}/>, roles: ALL_USER_ROLES },
    { view: 'event-tickets', label: 'Event Tickets', icon: <Ticket size={28}/>, roles: ALL_USER_ROLES },
    { view: 'contractfactory', label: 'Contracts & Agreements', icon: <FileSignature size={28}/>, roles: ALL_USER_ROLES },
    { view: 'proposals', label: 'Proposals', icon: <FileSignature size={28}/>, roles: ALL_USER_ROLES },
    { view: 'roster-bookings', label: 'Roster Bookings', icon: <Calendar size={28}/>, roles: ALL_USER_ROLES },
    { view: 'roster', label: 'Roster', icon: <Users size={28}/>, roles: [UserRole.ARTIST, UserRole.DAO_MEMBER, UserRole.ADMIN, UserRole.SYSTEM_ADMIN_LIVE] },
    { view: 'onboarding', label: 'Onboarding', icon: <UserPlus size={28}/>, roles: [UserRole.ADMIN, UserRole.SYSTEM_ADMIN_LIVE] },
    { view: 'support-requests', label: 'All Support', icon: <MessageSquare size={28}/>, roles: [UserRole.ADMIN, UserRole.SYSTEM_ADMIN_LIVE] },
    { view: 'my-support-tickets', label: 'My Support', icon: <MessageSquare size={28}/>, roles: ALL_USER_ROLES },
    { view: 'hrds', label: 'HRDs', icon: <Users size={28}/>, roles: [UserRole.ADMIN, UserRole.SYSTEM_ADMIN_LIVE] },
    { view: 'tables', label: 'All Tables', icon: <Table size={28}/>, roles: [UserRole.ADMIN, UserRole.SYSTEM_ADMIN_LIVE] },
    { view: 'treasury', label: 'Treasury', icon: <Landmark size={28}/>, roles: [UserRole.DAO_GOVERNOR, UserRole.ADMIN, UserRole.SYSTEM_ADMIN_LIVE] },
    { view: 'dao-members', label: 'DAO Members', icon: <Users size={28}/>, roles: [UserRole.DAO_MEMBER, UserRole.DAO_GOVERNOR, UserRole.ADMIN, UserRole.SYSTEM_ADMIN_LIVE] },
    { view: 'dao-governors', label: 'DAO Governors', icon: <Users size={28}/>, roles: [UserRole.DAO_GOVERNOR, UserRole.ADMIN, UserRole.SYSTEM_ADMIN_LIVE] },
  ];

  interface ActionCardProps {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    onClick: () => void;
  }

  const ActionCard: React.FC<ActionCardProps> = ({ icon, title, subtitle, onClick }) => (
    <div
      onClick={onClick}
      className="bg-kala-800/50 border border-kala-700 rounded-xl p-5 text-center flex flex-col items-center justify-center gap-3 cursor-pointer group hover:bg-kala-800 transition-colors transform hover:-translate-y-1 duration-300"
    >
      <div className="text-cyan-400 group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <div>
        <h4 className="font-bold text-white group-hover:text-cyan-300 transition-colors">{title}</h4>
        <p className="text-xs text-kala-400">{subtitle}</p>
      </div>
    </div>
  );

const PageWrapper = ({ onNavigate, children, title }) => (
    <div className="space-y-8 animate-in fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white mb-1">{title}</h1>
        <button
          onClick={() => onNavigate('dashboard')}
          className="bg-kala-secondary hover:bg-cyan-400 text-kala-900 font-bold py-2 px-4 rounded"
        >
          Back to Dashboard
        </button>
      </div>
      {children}
    </div>
)

const TablesPage: React.FC<TablesPageProps> = ({ activeView, onNavigate, currentUserRole, currentUserId, requests, onStatusChange, onDelete }) => {
  const handleNavigation = (view: string) => {
    if (view === 'dashboard') {
      onNavigate('/dashboard');
    } else if (view === 'roster') {
      onNavigate('/roster');
    } else {
      onNavigate(`/tables/${view}`);
    }
  };

  const passProps = { onNavigate: handleNavigation, currentUserRole, currentUserId };
  
  const normalizeRole = (role: string) => role.toLowerCase().replace(/_|-|\s/g, '');

  const navItemsForUser = allTableNavItems.filter(item => 
    item.roles.map(r => normalizeRole(r)).includes(normalizeRole(currentUserRole))
  );

  const renderContent = () => {
    switch (activeView) {
        case 'artists':
            return <ArtistPage {...passProps} />;
        case 'dao-members':
            return <DAOMemberPage {...passProps} />;
        case 'dao-proposals':
            return <DAOProposalPage {...passProps} />;
        case 'dao-governors':
            return <DaoGovernorPage {...passProps} />;
        case 'events':
            return <PageWrapper onNavigate={handleNavigation} title="Events"><EventPage {...passProps} /></PageWrapper>;
        case 'event-tickets':
            return <EventTicketPage {...passProps} />;
        case 'hrds':
            return <HRDPage onNavigate={handleNavigation} />;
        case 'organizers':
            return <OrganizerPage {...passProps} />;
        case 'revellers':
            return <RevellerPage {...passProps} />;
        case 'service-providers':
            return <ServiceProviderPage {...passProps} />;
        case 'sponsors':
            return <SponsorPage {...passProps} />;
        case 'support-requests':
            return <SupportRequestsPage {...passProps} />;
        case 'my-support-tickets':
            return <MySupportTickets onNavigate={handleNavigation} />;
        case 'treasury':
            return <PageWrapper onNavigate={handleNavigation} title="Treasury"><TreasuryPage {...passProps} /></PageWrapper>;
        case 'venues':
            return <VenuePage {...passProps} />;
        case 'proposals':
            return <ProposalPage {...passProps} />;
        case 'roster-bookings':
            return <RosterBookingPage {...passProps} />;
        case 'marketplace':
        case 'marketplace-table':
            return <MarketplacePage {...passProps} />;
        case 'roster':
            return <RosterPage {...passProps} />;
        case 'roster_management':
            return <RosterRealDataTable currentUserId={currentUserId} />;
        case 'roster_analytics':
            return <RosterAnalyticsPage onNavigate={handleNavigation} />;
        case 'contractfactory':
            return <PageWrapper onNavigate={handleNavigation} title="Contracts & Agreements"><ContractFactoryRealDataTable {...passProps} /></PageWrapper>;
        case 'contracts':
            return <ContractsDashboard />;
        case 'onboarding':
            return <PageWrapper onNavigate={handleNavigation} title="Onboarding"><OnboardingPage requests={requests} onStatusChange={onStatusChange} onDelete={onDelete} currentUserRole={currentUserRole} /></PageWrapper>;
        default:
            return (
                <div className="space-y-8">
                    <div className="flex justify-between items-center">
                        <h1 className="text-3xl font-bold text-white">Data Management</h1>
                        <button 
                            onClick={() => handleNavigation('dashboard')}
                            className="bg-kala-secondary hover:bg-cyan-400 text-kala-900 font-bold py-2 px-4 rounded"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {navItemsForUser.map(item => (
                            <ActionCard
                                key={item.view}
                                icon={item.icon}
                                title={item.label}
                                subtitle={`View ${item.label} Data`}
                                onClick={() => handleNavigation(item.view)}
                            />
                        ))}
                    </div>
                </div>
            );
      }
  };

  return <div className="animate-in fade-in">{renderContent()}</div>;
};

export default TablesPage;
